import { t } from '@/i18n'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, ChevronsUpDown, Search } from 'lucide-react'

export interface SearchableOption {
  value: number | string
  label: string
  /** Shown under the label — a SKU, a phone number, whatever tells two similar rows apart. */
  hint?: string
}

/**
 * A picker that can be typed into.
 *
 * A native select is fine for a handful of choices and unusable for a catalogue: finding one
 * product among six hundred means scrolling a list with no way to narrow it, which is what had
 * people giving up and keying a barcode instead. Anything long enough to scroll uses this;
 * genuinely short lists stay as a plain select, where a search box would only be in the way.
 */
export function SearchableSelect({
  label,
  value,
  options,
  onChange,
  placeholder = 'Choose…',
  hint,
  error,
  disabled,
  required,
}: {
  label: string
  value: number | string | ''
  options: SearchableOption[]
  onChange: (value: number | string | '') => void
  placeholder?: string
  hint?: string
  error?: string
  disabled?: boolean
  required?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const container = useRef<HTMLDivElement>(null)

  const selected = options.find((option) => option.value === value)

  const matches = useMemo(() => {
    const needle = term.trim().toLowerCase()
    if (needle === '') return options

    // Matches the hint as well as the label, so a SKU or a phone number finds the row.
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) ||
        (option.hint?.toLowerCase().includes(needle) ?? false),
    )
  }, [options, term])

  // Closing on an outside click rather than on blur: blur fires before the option's click lands,
  // which would shut the list every time somebody tried to pick from it.
  useEffect(() => {
    if (!open) return

    function onPointerDown(event: MouseEvent | TouchEvent) {
      if (!container.current?.contains(event.target as Node)) setOpen(false)
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
    }
  }, [open])

  useEffect(() => {
    if (open) setTerm('')
  }, [open])

  return (
    <div ref={container} className="relative flex flex-col gap-1.5">
      <span className="text-sm font-medium">
        {label}
        {required && <span className="text-danger-500"> *</span>}
      </span>

      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`surface-raised flex touch-target w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm disabled:opacity-50 ${
          error ? 'border-danger-500' : ''
        }`}
      >
        <span className={selected ? 'truncate' : 'truncate text-[var(--text-secondary)]'}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-[var(--text-secondary)]" />
      </button>

      {open && (
        <div className="surface-raised absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-lg border shadow-lg">
          <div className="flex items-center gap-2 border-b border-[var(--border-subtle)] px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-[var(--text-secondary)]" />
            <input
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder={t('Type to filter…')}
              autoFocus
              className="w-full bg-transparent text-sm outline-none"
            />
          </div>

          <ul role="listbox" className="max-h-64 overflow-y-auto">
            {matches.length === 0 ? (
              <li className="px-3 py-3 text-sm text-[var(--text-secondary)]">{t('Nothing matches.')}</li>
            ) : (
              matches.map((option) => (
                <li key={String(option.value)}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.value === value}
                    onClick={() => {
                      onChange(option.value)
                      setOpen(false)
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[var(--surface-sunken)]"
                  >
                    <Check
                      className={`h-4 w-4 shrink-0 ${
                        option.value === value ? 'text-[var(--accent)]' : 'opacity-0'
                      }`}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{option.label}</span>
                      {option.hint && (
                        <span className="block truncate text-xs text-[var(--text-secondary)]">
                          {option.hint}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}

      {error ? (
        <p className="text-sm text-danger-500">{error}</p>
      ) : hint ? (
        <p className="text-sm text-[var(--text-secondary)]">{hint}</p>
      ) : null}
    </div>
  )
}
